import { DateTime } from "luxon";

export const activityEndDateTime = (raw: string | null | undefined): DateTime | null => {
  if (!raw) return null;
  const dt = DateTime.fromSQL(raw);
  return dt.isValid ? dt : null;
};

export const formatActivityDate = (raw: string | null | undefined): string => activityEndDateTime(raw)?.toFormat("d MMMM yyyy") ?? "";

export const formatActivityTime = (raw: string | null | undefined): string => activityEndDateTime(raw)?.toFormat("HH'\u202fh\u202f'mm") ?? "";

// Dates d'observation : l'API renvoie tantôt du SQL (`yyyy-MM-dd HH:mm:ss`), tantôt de l'ISO.
export const formatObservationDate = (raw: string | null | undefined): string => {
  if (!raw) return "";
  const dt = DateTime.fromSQL(raw).isValid ? DateTime.fromSQL(raw) : DateTime.fromISO(raw);
  return dt.isValid ? dt.toFormat("d MMMM yyyy") : "";
};

// « min » (pas « m ») pour ne pas collisionner avec les mètres (profondeur) affichés à côté.
export const formatSeconds = (totalSeconds: number) => {
  const total = Math.max(0, Math.trunc(totalSeconds));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  if (hours > 0) {
    if (minutes === 0 && seconds === 0) return `${hours}\u202fh`;
    if (seconds === 0) return `${hours}\u202fh\u202f${minutes}\u202fmin`;
    return `${hours}\u202fh\u202f${minutes}\u202fmin\u202f${seconds}\u202fs`;
  }
  if (minutes > 0) return seconds === 0 ? `${minutes}\u202fmin` : `${minutes}\u202fmin\u202f${seconds}\u202fs`;
  return `${seconds}\u202fs`;
};

const KG_FORMATTER = new Intl.NumberFormat("fr-FR", {
  style: "unit",
  unit: "kilogram",
  unitDisplay: "short",
  maximumFractionDigits: 1,
});

export const formatKg = (value: number) => KG_FORMATTER.format(value);

const MM_FORMATTER = new Intl.NumberFormat("fr-FR", {
  style: "unit",
  unit: "millimeter",
  unitDisplay: "short",
  maximumFractionDigits: 0,
});

export const formatMm = (value: number) => MM_FORMATTER.format(value);

const DECIMAL_FORMATTER = new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 1 });

export const formatDecimal = (value: number | null | undefined) => (value == null ? "" : DECIMAL_FORMATTER.format(value));

export const memberFullName = (member: { fullName?: string | null; firstName?: string | null; lastName?: string | null }) =>
  member.fullName?.trim() || [member.firstName, member.lastName].filter(Boolean).join(" ").trim();

const BYTES_PER_KILOBYTE = 1024;
const BYTES_PER_MEGABYTE = BYTES_PER_KILOBYTE * 1024;

const KILOBYTE_FORMATTER = new Intl.NumberFormat("fr-FR", {
  style: "unit",
  unit: "kilobyte",
  unitDisplay: "short",
  maximumFractionDigits: 0,
});

const MEGABYTE_FORMATTER = new Intl.NumberFormat("fr-FR", {
  style: "unit",
  unit: "megabyte",
  unitDisplay: "short",
  maximumFractionDigits: 1,
});

export const formatFileSize = (bytes: number) => {
  const safeBytes = Number.isFinite(bytes) ? Math.max(0, bytes) : 0;
  return safeBytes < BYTES_PER_MEGABYTE
    ? KILOBYTE_FORMATTER.format(safeBytes / BYTES_PER_KILOBYTE)
    : MEGABYTE_FORMATTER.format(safeBytes / BYTES_PER_MEGABYTE);
};

// Intl pose une espace insécable normale devant « % » ; le projet utilise la fine insécable
// (U+202F) entre un nombre et son symbole.
export const formatPercent = (value: number) => `${DECIMAL_FORMATTER.format(value)}\u202f%`;
